// Pure helpers for GamePerformanceReport: date ranges, merging several games'
// series by period, and assigning each compared game a stable colour.

export const MAX_COMPARE_GAMES = 5;

// Fixed-order categorical palette (identity, not magnitude): blue and yellow
// first so a one- or two-game view looks as it did before multi-compare, then
// the remaining brand tokens minus brand-purple, which sits too close to
// brand-blue for colourblind readers.
export const GAME_COLORS = ['#3861FB', '#FFD22F', '#00BD71', '#E21351', '#d4a843'];

export function todayUtc() {
  return new Date(new Date().toISOString().slice(0, 10));
}

export function rangeForLastDays(days) {
  const to = todayUtc();
  const from = new Date(to);
  from.setUTCDate(from.getUTCDate() - days);
  return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
}

// [from, to) against group_created_at — `to` here is a calendar date, so the
// request's upper bound is midnight the day AFTER it, to include that whole day.
export function toRequestRange(range) {
  const to = new Date(`${range.to}T00:00:00.000Z`);
  to.setUTCDate(to.getUTCDate() + 1);
  return { from: `${range.from}T00:00:00.000Z`, to: to.toISOString() };
}

export function periodLabel(iso, bucket) {
  const d = new Date(iso);
  if (bucket === 'month') return d.toLocaleDateString('en-GB', { month: 'short', year: 'numeric', timeZone: 'UTC' });
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', timeZone: 'UTC' });
}

// A game with no transactions in a bucket has no row for it, which means
// zero activity — except payout, which is undefined without turnover.
export function emptyPoint(periodStart) {
  return { period_start: periodStart, actives: 0, n_bets: 0, turnover_eur: 0, ggr_eur: 0, payout_pct: null };
}

// Lines up every game's points by period_start: one row per period any game
// has data for, carrying each game's point (or an empty one) under its key.
export function mergeSeries(seriesByKey) {
  const maps = Object.entries(seriesByKey).map(([key, points]) => [key, new Map(points.map(p => [p.period_start, p]))]);
  const periods = new Set();
  maps.forEach(([, m]) => m.forEach((_, ps) => periods.add(ps)));
  return Array.from(periods)
    .sort((x, y) => new Date(x) - new Date(y))
    .map(ps => ({
      period_start: ps,
      values: Object.fromEntries(maps.map(([key, m]) => [key, m.get(ps) || emptyPoint(ps)])),
    }));
}

// First palette colour no selected game is using, so removing a game never
// recolours the others.
export function nextColor(selected) {
  const used = new Set(selected.map(s => s.color));
  return GAME_COLORS.find(c => !used.has(c)) || GAME_COLORS[selected.length % GAME_COLORS.length];
}

// Returns a new selection with game added (ignored if already present or the
// selection is full).
export function addGame(selected, game) {
  if (selected.length >= MAX_COMPARE_GAMES) return selected;
  if (selected.some(s => s.game.unique_game_id === game.unique_game_id)) return selected;
  return [...selected, { game, color: nextColor(selected) }];
}

export function removeGame(selected, uniqueGameId) {
  return selected.filter(s => s.game.unique_game_id !== uniqueGameId);
}

// Numeric comparator that always sinks nulls (e.g. payout_pct on a
// zero-turnover bucket) to the end, regardless of sort direction.
export function compareNumeric(a, b, dir) {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  return dir === 'asc' ? a - b : b - a;
}
